import {copyToClipboard, IconButton} from 'platform/components';
import {Box, HStack, Icon, ThemeIconKey} from 'platform/foundation';

import {isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import type {SellerContact} from '../../../types/SellerContact';
import {ContactAction} from './ContactAction';
import {TruncatedText} from './TruncatedText';

interface ContactRowProps extends TestIdProps {
  icon: ThemeIconKey;
  /** E.g. "Phone", read out for the action icon. */
  actionLabel: string;
  contact: SellerContact;
}

/**
 * A contact on one line: the action icon, the value as plain text that can be selected with the
 * cursor, and a copy button. Any number of contacts just adds rows, so nothing breaks the layout.
 */
export function ContactRow(props: ContactRowProps) {
  return (
    <HStack spacing={3} align="center">
      {isNotNil(props.contact.href) ? (
        <ContactAction href={props.contact.href} aria-label={props.actionLabel}>
          <Icon value={props.icon} size={4} color="general.accent" />
        </ContactAction>
      ) : (
        // An unreadable contact is still shown, just not linked - see SellerContact.
        <Box padding={2}>
          <Icon value={props.icon} size={4} color="text.secondary" />
        </Box>
      )}
      <Box flex={1} minWidth={0}>
        <TruncatedText
          value={props.contact.label}
          isAlternative
          data-testid={props['data-testid']}
        />
      </Box>
      <IconButton
        icon="content/copy"
        size="small"
        priority="tertiary"
        aria-label={i18n.t('general.actions.copyToClipboard')}
        onClick={() => copyToClipboard(props.contact.label)}
        data-testid={suffixTestId('copy', props)}
      />
    </HStack>
  );
}
